import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Megaphone, Image, MoreHorizontal, Trash2, Plus, Search, FileJson, Layers, Zap, Package, Users, Image as ImageIcon, Sparkles, Film, UploadCloud, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { BUCKETS, bucketOf, type BucketId } from "@/lib/static-ad-buckets";
import { BUCKET_VISUALS } from "@/lib/bucket-visuals";
import { mapJsonToAdUnit, parseBulkAdUnits, type BulkSourceFormat, type BulkParsedRow } from "@/lib/bulk-ad-unit-parser";
import React from "react";
import { Link } from "wouter";
import { useState, useMemo, useRef, useEffect } from "react";
import type { AdUnitCatalogItem, AdUnitType } from "@shared/schema";
import { type AdImageRecord, displayUrl, normalizeExampleImage, thumbUrl } from "@shared/adImage";
import { apiRequest, queryClient } from "@/lib/queryClient";

function toImageRecords(raw: unknown): AdImageRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => normalizeExampleImage(entry)).filter((r): r is AdImageRecord => r != null);
}
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";

const FUNNEL_COLORS: Record<string, string> = {
  Awareness: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  Knowledge: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  Liking: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  Preference: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  Conviction: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  Purchase: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
};

const FUNNEL_STAGES = ["Awareness", "Knowledge", "Liking", "Preference", "Conviction", "Purchase"] as const;

const BUCKET_LABELS: Record<string, string> = Object.fromEntries(BUCKETS.map((b) => [b.id, b.label]));

function AdUnitCard({ adUnit, brandId, onDeleteClick, isAdmin, canCurateApproval, onApprovalChange, approvalPending, selected, onSelectionChange, selectionDisabled }: {
  adUnit: AdUnitCatalogItem;
  brandId: string;
  onDeleteClick: (adUnit: AdUnitType) => void;
  isAdmin: boolean;
  canCurateApproval: boolean;
  onApprovalChange: (adUnitId: string, approved: boolean) => void;
  approvalPending: boolean;
  selected: boolean;
  onSelectionChange: (adUnitId: string, selected: boolean) => void;
  selectionDisabled: boolean;
}) {
  const imageRecords = toImageRecords(adUnit.exampleImages);
  const firstImage = thumbUrl(displayUrl(imageRecords[0] ?? null), 480);
  const imageCount = imageRecords.length;
  const funnelColor = FUNNEL_COLORS[adUnit.funnelStage || ""] || "bg-muted text-muted-foreground";
  const isApprovedForBrand = adUnit.isApprovedForBrand;

  return (
    <div className="relative group" data-testid={`card-ad-unit-${adUnit.id}`}>
      <Link href={`/brands/${brandId}/ad-units/${adUnit.id}`}>
        <Card className="cursor-pointer overflow-hidden border-border/40 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 h-full">
          <div className="aspect-[4/3] bg-muted/30 relative overflow-hidden">
            {firstImage ? (
              <img
                src={firstImage}
                alt={adUnit.name}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                data-testid={`thumbnail-img-${adUnit.id}`}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted/50 to-muted/20">
                <Megaphone className="h-10 w-10 text-muted-foreground/20" />
              </div>
            )}

            {imageCount > 1 && (
              <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-black/60 backdrop-blur-sm text-white text-[10px] font-medium rounded-full px-2 py-0.5" data-testid={`img-count-${adUnit.id}`}>
                <Image className="h-3 w-3" /> {imageCount}
              </div>
            )}

            {canCurateApproval && (
              <div className="absolute top-2.5 right-2.5 z-10" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <Checkbox
                  checked={selected}
                  onCheckedChange={(checked) => onSelectionChange(adUnit.id, checked === true)}
                  disabled={selectionDisabled}
                  aria-label={`Select ${adUnit.name} for Ads Express bulk actions`}
                  className="h-5 w-5 border-white/80 bg-background/90 shadow-sm data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                  data-testid={`checkbox-select-ad-unit-${adUnit.id}`}
                />
              </div>
            )}

            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/50 to-transparent" />
          </div>

          <div className="p-3.5 space-y-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge
                variant="secondary"
                className="text-[10px] rounded-full px-2 capitalize font-medium"
                data-testid={`badge-format-${adUnit.id}`}
              >
                {adUnit.formatType || "other"}
              </Badge>
              {adUnit.funnelStage && (
                <Badge variant="secondary" className={`text-[10px] rounded-full px-2 font-medium border-0 ${funnelColor}`} data-testid={`badge-funnel-${adUnit.id}`}>
                  {adUnit.funnelStage}
                </Badge>
              )}
              {canCurateApproval && (
                <Badge
                  variant="secondary"
                  className={`text-[10px] rounded-full px-2 font-medium border-0 ${
                    isApprovedForBrand
                      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                      : "bg-amber-500/15 text-amber-800 dark:text-amber-200"
                  }`}
                  data-testid={`badge-ads-express-default-${adUnit.id}`}
                >
                  {isApprovedForBrand ? "Default in Ads Express" : "Not default in Ads Express"}
                </Badge>
              )}
            </div>

            <h3 className="font-semibold text-sm tracking-tight leading-snug line-clamp-2" data-testid={`text-name-${adUnit.id}`}>
              {adUnit.name}
            </h3>

            {adUnit.strategicRole && (
              <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed" data-testid={`text-role-${adUnit.id}`}>
                {adUnit.strategicRole}
              </p>
            )}
          </div>
        </Card>
      </Link>

      {isAdmin && (
        <div className="absolute top-2.5 left-2.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 hover:text-white border-0"
                data-testid={`btn-ad-unit-menu-${adUnit.id}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              {canCurateApproval && (
                <DropdownMenuItem
                  className="gap-2 cursor-pointer"
                  disabled={approvalPending}
                  data-testid={`btn-toggle-ads-express-default-${adUnit.id}`}
                  onSelect={() => onApprovalChange(adUnit.id, !isApprovedForBrand)}
                >
                  {approvalPending
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : isApprovedForBrand
                      ? <XCircle className="h-3.5 w-3.5 text-amber-600" />
                      : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                  {isApprovedForBrand ? "Remove from Ads Express default" : "Make default in Ads Express"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-destructive focus:text-destructive gap-2 cursor-pointer"
                data-testid={`btn-delete-ad-unit-${adUnit.id}`}
                onSelect={() => onDeleteClick(adUnit)}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}

export default function AdUnitsPage({ mode = "static" }: { mode?: "static" | "ugc" | "pixar" } = {}) {
  const { brandId } = useParams<{ brandId: string }>();
  const { user } = useAuth();
  const isAdmin = !!user?.isMaster || !!user?.isSuperuser;
  const isUgc = mode === "ugc";
  const isPixar = mode === "pixar";
  const canBulkImport = mode !== "static" || !!user?.isMaster;
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [createTab, setCreateTab] = useState<string>("json");
  const [formName, setFormName] = useState("");
  const [formFormat, setFormFormat] = useState("");
  const [formFunnel, setFormFunnel] = useState("");
  const [formBucket, setFormBucket] = useState<string>(mode === "pixar" ? "pixar" : "");
  const [jsonText, setJsonText] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [bulkFormat, setBulkFormat] = useState<BulkSourceFormat>("auto");
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkDragging, setBulkDragging] = useState(false);
  const [bulkImport, setBulkImport] = useState<{ active: boolean; done: number; total: number }>({ active: false, done: 0, total: 0 });
  const bulkFileInputRef = useRef<HTMLInputElement | null>(null);
  const [bulkImages, setBulkImages] = useState<{ id: string; file: File; name: string; previewUrl: string }[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<AdUnitType | null>(null);
  const [search, setSearch] = useState("");
  const [filterFormat, setFilterFormat] = useState<string>("all");
  const [filterFunnel, setFilterFunnel] = useState<string>("all");
  const [selectedApprovalIds, setSelectedApprovalIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedApprovalIds(new Set());
  }, [brandId, mode]);

  const { data: adUnits, isLoading } = useQuery<AdUnitCatalogItem[]>({
    queryKey: [`/api/brands/${brandId}/ad-units`],
  });

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const name = String(data.name || "Untitled");
      const slug = name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      const res = await apiRequest("POST", `/api/brands/${brandId}/ad-units`, { ...data, name, slug, exampleImages: [] });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units"] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
      setOpen(false);
      setFormName(""); setFormFormat(""); setFormFunnel(""); setFormBucket(mode === "pixar" ? "pixar" : ""); setJsonText("");
      toast({ title: "Ad unit created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleJsonCreate = () => {
    if (!jsonText.trim()) return;
    try {
      const data = JSON.parse(jsonText);
      const mapped = mapJsonToAdUnit(data);
      if (!mapped.name) {
        toast({ title: "Name required", description: "The JSON must contain an 'ad_unit_name' or 'name' field.", variant: "destructive" });
        return;
      }
      createMutation.mutate(mapped);
    } catch (e: any) {
      toast({ title: "Invalid JSON", description: e.message, variant: "destructive" });
    }
  };

  const resetBulk = () => {
    setBulkText(""); setBulkFormat("auto"); setBulkFileName("");
    setBulkImages((prev) => { prev.forEach((i) => URL.revokeObjectURL(i.previewUrl)); return []; });
    setBulkDragging(false); setBulkImport({ active: false, done: 0, total: 0 });
  };

  const bulkParse = useMemo(() => parseBulkAdUnits(bulkText, bulkFormat), [bulkText, bulkFormat]);
  const bulkValidRows = bulkParse.rows.filter((r) => r.valid);

  // Dropped/selected image files each become one template (name from the
  // filename), with the image itself uploaded as the template's example image.
  const imageRows = useMemo(
    () => bulkImages.map((img) => {
      const name = img.name.trim();
      return { id: img.id, file: img.file, previewUrl: img.previewUrl, name, formatType: "static", valid: name.length > 0, reason: name.length > 0 ? undefined : "Missing name" };
    }),
    [bulkImages],
  );
  const imageValidRows = imageRows.filter((r) => r.valid);
  const totalValidCount = bulkValidRows.length + imageValidRows.length;
  const totalRowCount = bulkParse.rows.length + imageRows.length;

  const IMAGE_EXT = /\.(png|avif|jpe?g|webp)$/i;

  const handleBulkFiles = (files: File[]) => {
    const textFiles = files.filter((f) => /\.(json|csv)$/i.test(f.name));
    const imageFiles = files.filter((f) => IMAGE_EXT.test(f.name));
    if (files.some((f) => !textFiles.includes(f) && !imageFiles.includes(f))) {
      toast({ title: "Unsupported file", description: "Upload a .json/.csv file or PNG, AVIF, JPG, or WebP images.", variant: "destructive" });
    }
    const textFile = textFiles[0];
    if (textFile) {
      const isCsv = textFile.name.toLowerCase().endsWith(".csv");
      const reader = new FileReader();
      reader.onload = () => {
        setBulkText(typeof reader.result === "string" ? reader.result : "");
        setBulkFormat(isCsv ? "csv" : "json");
        setBulkFileName(textFile.name);
      };
      reader.onerror = () => toast({ title: "Could not read file", variant: "destructive" });
      reader.readAsText(textFile);
    }
    if (imageFiles.length > 0) {
      const additions = imageFiles.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        name: file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim(),
        previewUrl: URL.createObjectURL(file),
      }));
      setBulkImages((prev) => [...prev, ...additions]);
    }
  };

  const removeBulkImage = (id: string) => {
    setBulkImages((prev) => {
      const target = prev.find((i) => i.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((i) => i.id !== id);
    });
  };

  const uploadImageFile = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch("/api/upload", { method: "POST", body: fd, credentials: "include" });
    if (!res.ok) throw new Error(`${res.status}: ${(await res.text()) || res.statusText}`);
    const json = await res.json();
    if (!json?.url) throw new Error("Upload did not return a URL");
    return json.url as string;
  };

  const handleBulkImport = async () => {
    const textValid = bulkParse.rows.filter((r) => r.valid);
    const imgValid = imageRows.filter((r) => r.valid);
    type ImportTask =
      | { kind: "text"; mapped: Record<string, unknown> }
      | { kind: "image"; name: string; file: File };
    const tasks: ImportTask[] = [
      ...textValid.map((r) => ({ kind: "text" as const, mapped: r.mapped })),
      ...imgValid.map((r) => ({ kind: "image" as const, name: r.name, file: r.file })),
    ];
    if (tasks.length === 0) return;
    const invalidCount = (bulkParse.rows.length - textValid.length) + (imageRows.length - imgValid.length);
    setBulkImport({ active: true, done: 0, total: tasks.length });
    let imported = 0;
    let failed = 0;
    let completed = 0;

    // Create rows concurrently with a small cap: fast for a batch without
    // firing every POST at the server at once. `completed` keeps the live
    // "X of N" counter accurate regardless of finish order. Image tasks first
    // upload the file (durable URL), then create the template with it as the
    // example image.
    const CONCURRENCY = 5;
    let cursor = 0;
    const worker = async () => {
      while (cursor < tasks.length) {
        const task = tasks[cursor++];
        try {
          if (task.kind === "image") {
            const url = await uploadImageFile(task.file);
            const name = task.name || "Untitled";
            const slug = name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
            await apiRequest("POST", `/api/brands/${brandId}/ad-units`, { name, slug, formatType: "static", exampleImages: [url] });
          } else {
            const name = String(task.mapped.name || "Untitled");
            const slug = name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
            await apiRequest("POST", `/api/brands/${brandId}/ad-units`, { ...task.mapped, name, slug, exampleImages: [] });
          }
          imported++;
        } catch {
          failed++;
        }
        completed++;
        setBulkImport((prev) => ({ ...prev, done: completed }));
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, tasks.length) }, () => worker()));

    const skipped = failed + invalidCount;
    queryClient.invalidateQueries({ queryKey: ["/api/ad-units"] });
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
    toast({
      title: `Imported ${imported}, skipped ${skipped}`,
      description: skipped > 0
        ? `${invalidCount} invalid row${invalidCount === 1 ? "" : "s"} skipped${failed > 0 ? `, ${failed} failed to create` : ""}.`
        : undefined,
      variant: imported === 0 ? "destructive" : undefined,
    });
    resetBulk();
    setOpen(false);
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => { await apiRequest("POST", `/api/ad-units/${id}/archive`); },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units"] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
      setDeleteTarget(null);
      toast({ title: "Ad unit deleted" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const approvalMutation = useMutation({
    mutationFn: async ({ adUnitId, approved }: { adUnitId: string; approved: boolean }) => {
      await apiRequest(
        approved ? "POST" : "DELETE",
        `/api/admin/ad-units/${adUnitId}/brands/${brandId}/approval`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
      toast({ title: "Ads Express default updated" });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not update Ads Express default",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const approveAllMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/admin/brands/${brandId}/ad-units/approve-accessible`);
      return response.json() as Promise<unknown[]>;
    },
    onSuccess: (approvals) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
      toast({
        title: "All accessible templates are defaults",
        description: `${approvals.length} template${approvals.length === 1 ? "" : "s"} will now appear by default in Ads Express.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not approve accessible templates",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const selectedApprovalMutation = useMutation({
    mutationFn: async ({ approved }: { approved: boolean }) => {
      const ids = Array.from(selectedApprovalIds);
      const results = await Promise.all(ids.map(async (adUnitId) => {
        try {
          await apiRequest(
            approved ? "POST" : "DELETE",
            `/api/admin/ad-units/${adUnitId}/brands/${brandId}/approval`,
          );
          return { adUnitId, ok: true };
        } catch {
          return { adUnitId, ok: false };
        }
      }));
      return {
        succeeded: results.filter((result) => result.ok).length,
        failedIds: results.filter((result) => !result.ok).map((result) => result.adUnitId),
      };
    },
    onSuccess: ({ succeeded, failedIds }, { approved }) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
      setSelectedApprovalIds(new Set(failedIds));
      const action = approved ? "made default" : "removed from the default";
      toast({
        title: failedIds.length > 0 ? "Some templates were not updated" : "Templates updated",
        description: `${succeeded} template${succeeded === 1 ? "" : "s"} ${action}${failedIds.length > 0 ? `; ${failedIds.length} failed and remain selected.` : "."}`,
        variant: failedIds.length > 0 ? "destructive" : undefined,
      });
    },
    onError: (error: Error) => toast({
      title: "Could not update selected templates",
      description: error.message,
      variant: "destructive",
    }),
  });

  // Pre-filter by mode: UGC page shows only formatType==='ugc'; Pixar page shows
  // only formatType==='pixar' (its own dedicated workflow, separate library);
  // Static page shows everything that isn't UGC or Pixar.
  const modeFiltered = adUnits?.filter(u => {
    if (isUgc) return u.formatType === "ugc";
    if (isPixar) return u.formatType === "pixar";
    return u.formatType !== "ugc" && u.formatType !== "pixar";
  }) || [];
  const formatTypes = [...new Set(modeFiltered.map(u => u.formatType).filter(Boolean))].sort();

  const filtered = modeFiltered.filter(u => {
    if (filterFormat !== "all" && u.formatType !== filterFormat) return false;
    if (filterFunnel !== "all" && u.funnelStage !== filterFunnel) return false;
    if (search && !u.name.toLowerCase().includes(search.toLowerCase()) &&
        !(u.strategicRole || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const hiddenTemplateCount = modeFiltered.filter(
    (unit) => !unit.isApprovedForBrand,
  ).length;
  const canCurateStaticDefaults = isAdmin && !isUgc && !isPixar;
  const visibleApprovalIds = filtered.map((unit) => unit.id);
  const selectedApprovalCount = selectedApprovalIds.size;
  const selectionBusy = selectedApprovalMutation.isPending;

  return (
    <>
      <div className="p-8 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap fade-in-up">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="text-ad-units-title">
              {isUgc ? "UGC Ad Units" : isPixar ? "Pixar Ad Units" : "Static Ad Units"}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {filtered.length} {isUgc ? "template" : "format"}{filtered.length !== 1 ? "s" : ""} in your library
            </p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              {canCurateStaticDefaults && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => approveAllMutation.mutate()}
                  disabled={approveAllMutation.isPending}
                  className="rounded-full"
                  data-testid="button-approve-all-accessible-ad-units"
                >
                  {approveAllMutation.isPending
                    ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                  Make all accessible default
                </Button>
              )}
              <Button onClick={() => setOpen(true)} className="rounded-full px-5" data-testid="button-create-ad-units">
                <Plus className="h-4 w-4 mr-1.5" /> {isUgc ? "New UGC Ad Unit" : isPixar ? "New Pixar Ad Unit" : "New Static Ad Unit"}
              </Button>
            </div>
          )}
        </div>

        {canCurateStaticDefaults && (
          <Card className="border-primary/20 bg-primary/[0.025]" data-testid="card-ads-express-template-curation">
            <div className="flex flex-col gap-3 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Ads Express defaults</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Templates marked “Default in Ads Express” appear by default, whether they are global or owned by this brand.
                  </p>
                </div>
              </div>
              <Badge
                variant="secondary"
                className={hiddenTemplateCount > 0
                  ? "w-fit bg-amber-500/15 text-amber-800 dark:text-amber-200"
                  : "w-fit bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"}
                data-testid="badge-hidden-ads-express-template-count"
              >
                {hiddenTemplateCount > 0
                  ? `${hiddenTemplateCount} hidden from default`
                  : "All accessible templates are defaults"}
              </Badge>
              </div>
              <div className="flex flex-col gap-2 border-t border-primary/10 pt-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{selectedApprovalCount} selected</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setSelectedApprovalIds(new Set(visibleApprovalIds))}
                    disabled={visibleApprovalIds.length === 0 || selectionBusy}
                    data-testid="button-select-visible-ad-units"
                  >
                    Select visible
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setSelectedApprovalIds(new Set())}
                    disabled={selectedApprovalCount === 0 || selectionBusy}
                    data-testid="button-clear-selected-ad-units"
                  >
                    Clear
                  </Button>
                </div>
                {selectedApprovalCount > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      className="h-8"
                      onClick={() => selectedApprovalMutation.mutate({ approved: true })}
                      disabled={selectionBusy}
                      data-testid="button-approve-selected-ad-units"
                    >
                      {selectionBusy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                      Make selected default
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => selectedApprovalMutation.mutate({ approved: false })}
                      disabled={selectionBusy}
                      data-testid="button-unapprove-selected-ad-units"
                    >
                      Remove selected from default
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        <div className="flex items-center gap-3 flex-wrap fade-in-up-delay-1">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search ad units..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 rounded-full"
              data-testid="input-search-ad-units"
            />
          </div>
          <Select value={filterFormat} onValueChange={setFilterFormat}>
            <SelectTrigger className="w-[150px] rounded-full text-xs h-9" data-testid="select-filter-format">
              <Megaphone className="h-3 w-3 mr-1.5 text-muted-foreground shrink-0" />
              <SelectValue placeholder="All Formats" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Formats</SelectItem>
              {formatTypes.map(f => (
                <SelectItem key={f} value={f!} className="capitalize">{f}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterFunnel} onValueChange={setFilterFunnel}>
            <SelectTrigger className="w-[160px] rounded-full text-xs h-9" data-testid="select-filter-funnel">
              <Layers className="h-3 w-3 mr-1.5 text-muted-foreground shrink-0" />
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {FUNNEL_STAGES.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i} className="overflow-hidden border-border/40">
                <Skeleton className="aspect-[4/3]" />
                <div className="p-3.5 space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              </Card>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          isUgc || isPixar ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 fade-in-up-delay-2">
              {filtered.map(adUnit => (
                <AdUnitCard
                  key={adUnit.id}
                  adUnit={adUnit}
                  brandId={brandId!}
                  onDeleteClick={setDeleteTarget}
                  isAdmin={isAdmin}
                  canCurateApproval={canCurateStaticDefaults}
                  onApprovalChange={(adUnitId, approved) => approvalMutation.mutate({ adUnitId, approved })}
                   approvalPending={approvalMutation.isPending && approvalMutation.variables?.adUnitId === adUnit.id}
                   selected={selectedApprovalIds.has(adUnit.id)}
                   onSelectionChange={(adUnitId, selected) => setSelectedApprovalIds((previous) => {
                     const next = new Set(previous);
                     if (selected) next.add(adUnitId); else next.delete(adUnitId);
                     return next;
                   })}
                   selectionDisabled={selectionBusy}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-8 fade-in-up-delay-2">
              {(() => {
                const map = new Map<BucketId, typeof filtered>();
                BUCKETS.forEach((b) => map.set(b.id, []));
                for (const u of filtered) {
                  const b = bucketOf(u.slug, u.name, (u as { adsExpressBucket?: string | null }).adsExpressBucket);
                  (map.get(b) ?? map.get("other")!).push(u);
                }
                const groups = BUCKETS
                  .map((b) => ({ bucket: b, units: map.get(b.id) ?? [] }))
                  .filter((g) => g.units.length > 0);
                return groups.map(({ bucket, units }) => {
                  const visual = BUCKET_VISUALS[bucket.id];
                  return (
                    <div key={bucket.id} data-testid={`section-bucket-${bucket.id}`}>
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={["h-9 w-9 rounded-lg flex items-center justify-center shrink-0", visual.accent].join(" ")}>
                            {visual.icon}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-base font-bold tracking-tight">{bucket.label}</h3>
                              <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-bold tabular-nums">
                                {units.length}
                              </Badge>
                            </div>
                            <p className="text-[11px] text-muted-foreground/80 mt-0.5 line-clamp-1">{bucket.description}</p>
                          </div>
                        </div>
                        <div className={["hidden sm:block h-px flex-1 ml-3 opacity-30", visual.bar].join(" ")} />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {units.map((adUnit) => (
                          <AdUnitCard
                            key={adUnit.id}
                            adUnit={adUnit}
                            brandId={brandId!}
                            onDeleteClick={setDeleteTarget}
                            isAdmin={isAdmin}
                            canCurateApproval={canCurateStaticDefaults}
                            onApprovalChange={(adUnitId, approved) => approvalMutation.mutate({ adUnitId, approved })}
                            approvalPending={approvalMutation.isPending && approvalMutation.variables?.adUnitId === adUnit.id}
                            selected={selectedApprovalIds.has(adUnit.id)}
                            onSelectionChange={(adUnitId, selected) => setSelectedApprovalIds((previous) => {
                              const next = new Set(previous);
                              if (selected) next.add(adUnitId); else next.delete(adUnitId);
                              return next;
                            })}
                            selectionDisabled={selectionBusy}
                          />
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )
        ) : (
          <Card className="border-dashed border-border/50">
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Megaphone className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold tracking-tight mb-1">No ad units yet</h3>
              <p className="text-sm text-muted-foreground">Get started by creating your first one</p>
            </div>
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o && bulkImport.active) return; setOpen(o); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="tracking-tight">Create Ad Unit</DialogTitle>
          </DialogHeader>
          <Tabs value={createTab === "bulk" && !canBulkImport ? "json" : createTab} onValueChange={setCreateTab} className="w-full">
            <TabsList className={`w-full grid ${canBulkImport ? "grid-cols-3" : "grid-cols-2"}`}>
              <TabsTrigger value="json" className="gap-1.5" data-testid="tab-json-import">
                <FileJson className="h-3.5 w-3.5" /> Paste JSON
              </TabsTrigger>
              {canBulkImport && (
                <TabsTrigger value="bulk" className="gap-1.5" data-testid="tab-bulk-import">
                  <UploadCloud className="h-3.5 w-3.5" /> Bulk Import
                </TabsTrigger>
              )}
              <TabsTrigger value="form" data-testid="tab-manual-form">
                Manual
              </TabsTrigger>
            </TabsList>
            <TabsContent value="json" className="mt-4 space-y-3">
              <p className="text-xs text-muted-foreground">Paste your ad unit JSON below. All recognized fields will be mapped automatically.</p>
              <Textarea
                placeholder='{"ad_unit_name": "...", "primary_funnel": "...", ...}'
                value={jsonText}
                onChange={e => setJsonText(e.target.value)}
                rows={12}
                className="font-mono text-xs"
                data-testid="textarea-json-import"
              />
              <DialogFooter>
                <Button variant="secondary" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button
                  onClick={handleJsonCreate}
                  disabled={!jsonText.trim() || createMutation.isPending}
                  className="rounded-full"
                  data-testid="button-import-json"
                >
                  {createMutation.isPending ? "Creating..." : "Import"}
                </Button>
              </DialogFooter>
            </TabsContent>
            {canBulkImport && (
            <TabsContent value="bulk" className="mt-4 space-y-3">
              <p className="text-xs text-muted-foreground">
                Paste a JSON array of templates, or drag/select <span className="font-medium">.json</span> / <span className="font-medium">.csv</span> files or <span className="font-medium">PNG / AVIF</span> images. Each image becomes a template named from its filename. Rows are previewed and validated before anything is created.
              </p>

              <div
                onDragOver={(e) => { e.preventDefault(); setBulkDragging(true); }}
                onDragLeave={() => setBulkDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setBulkDragging(false);
                  const files = Array.from(e.dataTransfer.files || []);
                  if (files.length) handleBulkFiles(files);
                }}
                onClick={() => bulkFileInputRef.current?.click()}
                className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed px-4 py-5 text-center cursor-pointer transition-colors ${bulkDragging ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40 hover:bg-muted/30"}`}
                data-testid="dropzone-bulk-import"
              >
                <UploadCloud className="h-6 w-6 text-muted-foreground/60" />
                <p className="text-xs font-medium">Drop .json, .csv, or image files (PNG, AVIF), or click to browse</p>
                {bulkFileName && (
                  <p className="text-[11px] text-primary" data-testid="text-bulk-filename">{bulkFileName}</p>
                )}
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  multiple
                  accept=".json,.csv,.png,.avif,.jpg,.jpeg,.webp,application/json,text/csv,image/png,image/avif,image/jpeg,image/webp"
                  className="hidden"
                  data-testid="input-bulk-file"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length) handleBulkFiles(files);
                    e.target.value = "";
                  }}
                />
              </div>

              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-border/60" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">or paste</span>
                <div className="h-px flex-1 bg-border/60" />
              </div>

              <Textarea
                placeholder='[{"ad_unit_name": "...", "primary_funnel": "...", "format_type": "static"}, ...]'
                value={bulkText}
                onChange={(e) => { setBulkText(e.target.value); setBulkFormat("auto"); setBulkFileName(""); }}
                rows={6}
                className="font-mono text-xs"
                data-testid="textarea-bulk-import"
              />

              {bulkParse.error && bulkText.trim() && (
                <p className="text-xs text-destructive" data-testid="text-bulk-error">{bulkParse.error}</p>
              )}

              {(bulkParse.rows.length > 0 || imageRows.length > 0) && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium" data-testid="text-bulk-summary">
                      {totalValidCount} valid · {totalRowCount - totalValidCount} invalid
                    </span>
                    <span className="text-muted-foreground">{totalRowCount} row{totalRowCount === 1 ? "" : "s"}</span>
                  </div>
                  <div className="max-h-60 overflow-y-auto rounded-lg border border-border/50">
                    <table className="w-full table-fixed text-xs">
                      <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                        <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                          <th className="px-2 py-1.5 font-medium">Name</th>
                          <th className="px-2 py-1.5 font-medium w-16">Format</th>
                          <th className="px-2 py-1.5 font-medium w-16">Funnel</th>
                          <th className="px-2 py-1.5 font-medium w-24">Category</th>
                          <th className="px-2 py-1.5 font-medium w-20">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkParse.rows.map((row) => {
                          const bucket = bucketOf(undefined, row.name, (row.mapped as { adsExpressBucket?: string | null }).adsExpressBucket);
                          return (
                            <tr key={row.index} className="border-t border-border/40" data-testid={`row-bulk-preview-${row.index}`}>
                              <td className="px-2 py-1.5 truncate" title={row.name || "(no name)"}>{row.name || <span className="text-muted-foreground italic">(no name)</span>}</td>
                              <td className="px-2 py-1.5 capitalize">{row.formatType}</td>
                              <td className="px-2 py-1.5">{row.funnelStage || <span className="text-muted-foreground">—</span>}</td>
                              <td className="px-2 py-1.5">{BUCKET_LABELS[bucket] || bucket}</td>
                              <td className="px-2 py-1.5">
                                {row.valid ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400" data-testid={`status-bulk-valid-${row.index}`}>
                                    <CheckCircle2 className="h-3 w-3" /> Valid
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-destructive" data-testid={`status-bulk-invalid-${row.index}`} title={row.reason}>
                                    <XCircle className="h-3 w-3" /> {row.reason || "Invalid"}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {imageRows.map((row) => {
                          const bucket = bucketOf(undefined, row.name, undefined);
                          return (
                            <tr key={row.id} className="border-t border-border/40" data-testid={`row-bulk-image-${row.id}`}>
                              <td className="px-2 py-1.5">
                                <div className="flex items-center gap-2">
                                  <img src={row.previewUrl} alt="" className="h-7 w-7 shrink-0 rounded object-cover border border-border/50" />
                                  <span className="min-w-0 flex-1 truncate" title={row.name || "(no name)"}>{row.name || <span className="text-muted-foreground italic">(no name)</span>}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeBulkImage(row.id)}
                                    disabled={bulkImport.active}
                                    className="text-muted-foreground hover:text-destructive disabled:opacity-40"
                                    title="Remove image"
                                    data-testid={`button-remove-bulk-image-${row.id}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </td>
                              <td className="px-2 py-1.5 capitalize">{row.formatType}</td>
                              <td className="px-2 py-1.5"><span className="text-muted-foreground">—</span></td>
                              <td className="px-2 py-1.5">{BUCKET_LABELS[bucket] || bucket}</td>
                              <td className="px-2 py-1.5">
                                {row.valid ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400" data-testid={`status-bulk-image-valid-${row.id}`}>
                                    <CheckCircle2 className="h-3 w-3" /> Valid
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-destructive" data-testid={`status-bulk-image-invalid-${row.id}`} title={row.reason}>
                                    <XCircle className="h-3 w-3" /> {row.reason || "Invalid"}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="secondary" onClick={() => { resetBulk(); setOpen(false); }} disabled={bulkImport.active} className="rounded-full">Cancel</Button>
                <Button
                  onClick={handleBulkImport}
                  disabled={totalValidCount === 0 || bulkImport.active}
                  className="rounded-full"
                  data-testid="button-bulk-import"
                >
                  {bulkImport.active ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Importing… {bulkImport.done} of {bulkImport.total}</>
                  ) : (
                    <>Import {totalValidCount > 0 ? totalValidCount : ""} {totalValidCount === 1 ? "template" : "templates"}</>
                  )}
                </Button>
              </DialogFooter>
            </TabsContent>
            )}
            <TabsContent value="form" className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Name *</Label>
                <Input value={formName} onChange={e => setFormName(e.target.value)} data-testid="input-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Format Type *</Label>
                <Select value={formFormat} onValueChange={setFormFormat}>
                  <SelectTrigger data-testid="select-formatType"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="static">Static Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="carousel">Carousel</SelectItem>
                    <SelectItem value="ugc">UGC</SelectItem>
                    <SelectItem value="meme">Meme</SelectItem>
                    <SelectItem value="pixar">Pixar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Funnel Stage</Label>
                <Select value={formFunnel} onValueChange={setFormFunnel}>
                  <SelectTrigger data-testid="select-funnelStage"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {FUNNEL_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Ads Express Category
                </Label>
                <Select value={formBucket} onValueChange={setFormBucket}>
                  <SelectTrigger data-testid="select-adsExpressBucket">
                    <SelectValue placeholder="Auto-detect from name" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUCKETS.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Where this template appears in the Ads Express picker. Leave
                  blank to auto-detect from the name.
                </p>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button
                  onClick={() => createMutation.mutate({ name: formName, formatType: formFormat, funnelStage: formFunnel || undefined, adsExpressBucket: formBucket || undefined })}
                  disabled={!formName.trim() || !formFormat || createMutation.isPending}
                  className="rounded-full"
                  data-testid="button-submit-form"
                >
                  {createMutation.isPending ? "Creating..." : "Create"}
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This ad unit will be removed from your library. Any campaigns that already reference it will keep their existing briefs, but this format will no longer appear in new campaign wizards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="btn-confirm-delete"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
